import React, { useState, useRef, useEffect } from 'react';
import { Container, Form, Button, Row, Col, InputGroup } from 'react-bootstrap';
import { postDatatoServer } from '../../utils/apiBase';
import { toast } from 'react-hot-toast';
import ContextHelper from '../../ContextHooks/ContextHelper';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { FormField } from '../formFields/FormField';
import ImageUploadModal from './ImageUploadModal';
import { useLocation, useNavigate } from 'react-router-dom';

function AddUser() {
  const { currentUser } = ContextHelper();
  const initialFormState = {
    userRole: 'Doctor',
    username: '',
    password: '',
    address: '',
    details: '',
    management: '',
    email: '',
    expiryDate: '',
    permissions: {
      add_study: false,
      patient_billing: false,
      patient_registration: false,
      report_approval: false,
      business_revenue: false,
      turnaround_time: false,
      tag_update: false,
      study_update: false,
      history_update: false,
      add_verifier: false,
    },
  };

  const [formState, setFormState] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [headerFile, setHeaderFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);
  const [signFile, setSignFile] = useState(null);
  const [fileErrors, setFileErrors] = useState({
    header: '',
    footer: '',
    sign: '',
  });
  const [showModal, setShowModal] = useState(false);
  const handleModalClose = () => setShowModal(false);
  const handleModalShow = () => setShowModal(true);
  const location = useLocation();
  const { selectedUser } = location.state || {};
  const navigate = useNavigate();

  const headerFileRef = useRef(null);
  const footerFileRef = useRef(null);
  const signFileRef = useRef(null);
  useEffect(() => {
    if (selectedUser) {
      const updatedPermissions = {
        add_study: selectedUser?.permissions?.add_study === 'true',
        patient_billing: selectedUser?.permissions?.patient_billing === 'true',
        patient_registration: selectedUser?.permissions?.patient_registration === 'true',
        report_approval: selectedUser?.permissions?.report_approval === 'true',
        business_revenue: selectedUser?.permissions?.business_revenue === 'true',
        turnaround_time: selectedUser?.permissions?.turnaround_time === 'true',
        tag_update: selectedUser?.permissions?.tag_update === 'true',
        study_update: selectedUser?.permissions?.study_update === 'true',
        history_update: selectedUser?.permissions?.history_update === 'true',
        add_verifier: selectedUser?.permissions?.add_verifier === 'true',
      };

      let validExpiryDate = '';
      const expiry = Number(selectedUser.expiryDate);
      if (!isNaN(expiry)) {
        const date = new Date(expiry);
        const currentYear = new Date().getFullYear();
        if (date.getFullYear() <= currentYear + 10) {
          validExpiryDate = expiry;
        }
      }

      setFormState((prev) => ({
        ...prev,
        username: selectedUser.username || '',
        password: selectedUser.password || '',
        address: selectedUser.address || '',
        details: selectedUser.details || '',
        email: selectedUser.email || '',
        expiryDate: validExpiryDate,
        userRole: selectedUser.type === 'admin' ? 'Doctor' : 'Agent',
        permissions: updatedPermissions,
      }));
    }
  }, [selectedUser]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name.startsWith('permissions.')) {
      const key = name.split('.')[1];
      setFormState((prevState) => ({
        ...prevState,
        permissions: {
          ...prevState.permissions,
          [key]: checked,
        },
      }));
    } else if (name === 'expiryDate' && value) {
      const selectedDate = new Date(value);
      const now = new Date();
      selectedDate.setHours(now.getHours());
      selectedDate.setMinutes(now.getMinutes());
      selectedDate.setSeconds(now.getSeconds());
      selectedDate.setMilliseconds(now.getMilliseconds());
      const timestamp = selectedDate.getTime();
      setFormState({
        ...formState,
        [name]: timestamp,
      });
    } else {
      setFormState({
        ...formState,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const validateFile = (file, type) => {
    if (formState.userRole === 'Agent') {
      return '';
    }

    const allowedTypes = ['image/jpeg'];
    const maxSize = 5 * 1024 * 1024;

    if (currentUser?.type === 'superadmin') {
      if (file) {
        if (!allowedTypes.includes(file.type)) {
          return 'Only JPEG images are allowed';
        }
        if (file.size > maxSize) {
          return 'File size should not exceed 5MB';
        }
      }
      return '';
    }

    if (!file) {
      return `${type} image is required`;
    }

    if (!allowedTypes.includes(file.type)) {
      return 'Only JPEG images are allowed';
    }

    if (file.size > maxSize) {
      return 'File size should not exceed 5MB';
    }

    return '';
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const renamedFile = new File([file], `${formState.username}_${type}.jpg`, { type: file.type });
      const validationError = validateFile(renamedFile, type);

      switch (type) {
        case 'header':
          setHeaderFile(validationError ? null : renamedFile);
          setFileErrors((prev) => ({ ...prev, header: validationError }));
          if (validationError) headerFileRef.current.value = '';
          break;
        case 'footer':
          setFooterFile(validationError ? null : renamedFile);
          setFileErrors((prev) => ({ ...prev, footer: validationError }));
          if (validationError) footerFileRef.current.value = '';
          break;
        case 'sign':
          setSignFile(validationError ? null : renamedFile);
          setFileErrors((prev) => ({ ...prev, sign: validationError }));
          if (validationError) signFileRef.current.value = '';
          break;
      }

      if (validationError) {
        toast.error(`${type} image error: ${validationError}`);
      }
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(formState);
    const fileValidationErrors = {};
    if (formState.userRole !== 'Agent') {
      if (currentUser?.type == 'Doctor') {
        if (currentUser?.type !== 'superadmin') {
          const headerError = validateFile(headerFile, 'Header');
          const footerError = validateFile(footerFile, 'Footer');

          if (headerError) fileValidationErrors.header = headerError;
          if (footerError) fileValidationErrors.footer = footerError;
        }
      } else {
        const signError = validateFile(signFile, 'Sign');
        if (signError) fileValidationErrors.sign = signError;
      }
    }

    setFileErrors(fileValidationErrors);
    setErrors(validationErrors);
    console.log(errors, fileValidationErrors);
    if (Object.keys(validationErrors).length === 0 && Object.keys(fileValidationErrors).length === 0) {
      const formData = new FormData();
      if (currentUser?.type === 'superadmin') {
        Object.entries(formState.permissions).forEach(([key, value]) => {
          formData.append(`permissions[${key}]`, value);
        });
      }
      if (formState.userRole !== 'Agent') {
        if (currentUser?.type !== 'superadmin') {
          if (signFile) formData.append('files', signFile);
        } else {
          if (headerFile) formData.append('files', headerFile);
          if (footerFile) formData.append('files', footerFile);
        }
      }

      formData.append('username', formState.username);
      formData.append('type', (currentUser?.type === 'superadmin' && 'admin') || formState.userRole);
      formData.append('isadmin', formState.userRole === 'Agent' ? 'false' : 'true');
      formData.append('password', formState.password);
      formData.append('address', formState.address);
      formData.append('details', formState.details);

      if (selectedUser) {
        formData.append('originalUsername', selectedUser.username);
        formData.append('update', 'true');
      }

      if (formState.userRole !== 'superadmin') {
        formData.append('email', formState.email);
      }

      if (formState.expiryDate) {
        formData.append('expiryDate', formState.expiryDate);
      }

      postDatatoServer({
        end_point: 'user2',
        body: formData,
        call_back: handleResponse,
        props: {
          header: true,
          token: currentUser?.token,
          contentType: 'multipart/form-data',
        },
      });
    }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.username.trim()) errors.username = 'Username is required';
    if (!values.address.trim()) errors.address = 'Address is required';

    if (currentUser?.type === 'admin' && formState.userRole == 'Doctor') {
      if (!values.details.trim()) errors.details = 'Doctor Details is required';
    }
    if (currentUser?.type === 'superadmin') {
      if (!values.expiryDate) {
        errors.expiryDate = 'Please select date';
      }
    }

    if (values.userRole !== 'superadmin') {
      if (!values.email) {
        errors.email = 'Email is required';
      } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(values.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (!values.password.trim()) {
      errors.password = 'Password is required';
    } else if (values.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    return errors;
  };
  const clearFiles = () => {
    setHeaderFile(null);
    setFooterFile(null);
    setSignFile(null);
    if (headerFileRef.current) headerFileRef.current.value = '';
    if (footerFileRef.current) footerFileRef.current.value = '';
    if (signFileRef.current) signFileRef.current.value = '';
  };

  const handleResponse = (res) => {
    if (res?.response && res?.status === 'success') {
      if (
        res.response?.message === 'duplicate username,already available' ||
        res.response?.message === 'user with this email, already available'
      ) {
        toast.error(res.response.message);
      } else {
        toast.success('User created successfully');
        navigate('/users');
        setFormState(initialFormState);
        clearFiles();
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      {currentUser?.type === 'admin' && (
        <ImageUploadModal
          show={showModal}
          handleClose={handleModalClose}
          username={formState.username}
          currentUser={currentUser}
          userData={{
            type: formState.userRole,
            password: formState.password,
            address: formState.address,
            email: formState.email,
            expiryDate: formState.expiryDate,
          }}
        />
      )}

      <section className="bg-white p-4 min-vh-100 d-flex justify-content-center">
        <Container fluid className="px-0">
          <Row className="justify-content-center">
            <Col>
              <div className="d-flex justify-content-between">
                <h3 className="base-color mb-4">{selectedUser ? 'Edit User' : 'Create User'}</h3>

                <div className="d-flex justify-content-center align-items-center gap-3">
                  {currentUser?.type === 'admin' && (
                    <>
                      <Button onClick={handleModalShow} className="login-btn base-color-bg w-auto py-2 px-4 text-white">
                        Upload Report Images
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Form noValidate onSubmit={handleSubmit}>
                <Row>
                  {currentUser?.type !== 'superadmin' && (
                    <FormField
                      label="User Role"
                      name="userRole"
                      type="radio"
                      value={formState.userRole}
                      onChange={handleChange}
                      options={['Doctor', 'Agent']}
                      error={errors.userRole}
                      defaultValue="Doctor"
                      required
                    />
                  )}

                  {currentUser?.type === 'superadmin' && (
                    <FormField
                      label="End Date"
                      name="expiryDate"
                      value={formState.expiryDate ? new Date(formState.expiryDate).toISOString().split('T')[0] : ''}
                      onChange={handleChange}
                      error={errors.expiryDate}
                      required
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  )}

                  <FormField
                    label="Username"
                    name="username"
                    value={formState?.username?.replace(/\s+/g, '')}
                    onChange={handleChange}
                    error={errors.username}
                    disabled={!!selectedUser}
                    required
                  />

                  {formState.userRole !== 'superadmin' && (
                    <FormField
                      label="Email"
                      name="email"
                      value={formState?.email?.replace(/\s+/g, '')}
                      onChange={handleChange}
                      error={errors.email}
                      required
                    />
                  )}

                  <Form.Group as={Col} lg={3} md={4} sm={6} className="mb-3">
                    <Form.Label>
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formState?.password?.replace(/\s+/g, '')}
                        onChange={handleChange}
                        isInvalid={!!errors.password}
                        required
                      />
                      <Button variant="outline-secondary" onClick={togglePasswordVisibility}>
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </Button>
                      <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  <Col lg={3} md={4} sm={6}>
                    <Form.Group as={Col} className="mb-3">
                      <Form.Label>
                        Address
                        <span className="text-danger"> *</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="address"
                        value={formState.address}
                        onChange={handleChange}
                        isInvalid={!!errors.address}
                        required
                      />
                      <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {formState.userRole !== 'Agent' && (
                    <>
                      {currentUser?.type !== 'superadmin' ? (
                        <>
                          <Col lg={3} md={4} sm={6}>
                            <Form.Group as={Col} className="mb-3">
                              <Form.Label>
                                Doctor Details
                                <span className="text-danger"> *</span>
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                name="details"
                                value={formState.details}
                                onChange={handleChange}
                                isInvalid={!!errors.details}
                                required
                              />
                              <Form.Control.Feedback type="invalid">{errors.details}</Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col lg={3} md={4} sm={6}>
                            <Form.Group as={Col} className="mb-3">
                              <Form.Label>
                                Doctor Sign Image
                                <span className="text-danger"> *</span>
                              </Form.Label>
                              <Form.Control
                                ref={signFileRef}
                                type="file"
                                onChange={(e) => handleFileChange(e, 'sign')}
                                accept="image/jpeg"
                                isInvalid={!!fileErrors.sign}
                                required
                              />
                              <Form.Control.Feedback type="invalid">{fileErrors.sign}</Form.Control.Feedback>
                              {signFile && (
                                <small className="text-muted d-block mt-1">Selected file: {signFile.name}</small>
                              )}
                            </Form.Group>
                          </Col>
                        </>
                      ) : (
                        <>
                          {!selectedUser && (
                            <>
                              <Col lg={3} md={4} sm={6}>
                                <Form.Group as={Col} className="mb-3">
                                  <Form.Label>
                                    Report Header Image
                                    <span className="text-danger"> *</span>
                                  </Form.Label>
                                  <Form.Control
                                    ref={headerFileRef}
                                    type="file"
                                    onChange={(e) => handleFileChange(e, 'header')}
                                    accept="image/jpeg"
                                    isInvalid={!!fileErrors.header}
                                    required
                                  />
                                  <Form.Control.Feedback type="invalid">{fileErrors.header}</Form.Control.Feedback>
                                  {headerFile && (
                                    <small className="text-muted d-block mt-1">Selected file: {headerFile.name}</small>
                                  )}
                                </Form.Group>
                              </Col>

                              <Col lg={3} md={4} sm={6}>
                                <Form.Group as={Col} className="mb-3">
                                  <Form.Label>
                                    Report Footer Image
                                    <span className="text-danger"> *</span>
                                  </Form.Label>
                                  <Form.Control
                                    ref={footerFileRef}
                                    type="file"
                                    onChange={(e) => handleFileChange(e, 'footer')}
                                    accept="image/jpeg"
                                    isInvalid={!!fileErrors.footer}
                                    required
                                  />
                                  <Form.Control.Feedback type="invalid">{fileErrors.footer}</Form.Control.Feedback>
                                  {footerFile && (
                                    <small className="text-muted d-block mt-1">Selected file: {footerFile.name}</small>
                                  )}
                                </Form.Group>
                              </Col>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Row>
                {currentUser?.type === 'superadmin' && (
                  <div className="mb-3">
                    <h5 className="fw-bold mb-2">Module Permissions</h5>
                    <Row className="gy-3">
                      {Object.entries(formState.permissions).map(([key, value]) => (
                        <Col xs={12} sm={6} md={4} lg={3} key={key}>
                          <Form.Check
                            type="switch"
                            id={`perm-${key}`}
                            name={`permissions.${key}`}
                            label={key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                            checked={value}
                            onChange={handleChange}
                            className="text-nowrap fs-16"
                          />
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
                <div>
                  <Button type="submit" className="login-btn fs-15 base-color-bg text-white fw-semibold">
                    {selectedUser ? 'Update User' : 'Create User'}
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

export default AddUser;
